import { JournalEntry, MindShift, DailyMoodEntry, Action } from '../context/JournalContext';
import { supabase } from '../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLIC_KEY =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface SophiaAskResponse {
  answer?: string;
  response: string;
  perspective_shift?: string;
  supporting_evidence?: Array<{
    quote: string;
    entry_id?: string;
    date?: string;
  }>;
  bridges?: {
    actions_to_create?: SophiaAction[];
    journal_prompt?: string;
  };
  follow_ups?: string[];
  assumptions?: string[];
  followUp?: string[];
  resources?: string[];
  creditStatus?: any;
  requestId?: string;
}

export interface SophiaInsightItem {
  title: string;
  content: string;
  why_fits?: string;
  duration_minutes?: number;
  effort_level?: 'low' | 'medium' | 'high';
  micro_steps?: string[];
}

export interface SophiaQuickResponse {
  subtype: 'prompt' | 'reframe' | 'action' | 'metaphor';
  content?: string;
  items?: SophiaInsightItem[];
  supporting_evidence?: Array<{
    quote: string;
    entry_id?: string;
    date?: string;
  }>;
  bridges?: {
    actions_to_create?: SophiaAction[];
    journal_prompt?: string;
  };
  prompt?: string;
  context?: string;
  reframe?: string;
  technique?: string;
  affirmation?: string;
  actions?: Array<{
    action: string;
    why: string;
  }>;
  encouragement?: string;
  metaphor?: string;
  explanation?: string;
  insight?: string;
  creditStatus?: any;
  requestId?: string;
}

export interface SophiaDeepDiveResponse {
  headline_summary?: string;
  summary: string;
  patterns: Array<{
    name: string;
    description: string;
    evidence_refs: string[];
    strength: 'emerging' | 'moderate' | 'strong';
    pattern?: string;
    evidence?: string;
    significance?: string;
  }>;
  triggers: Array<{
    situation: string;
    emotional_shift: string;
    example_entry: string;
    trigger?: string;
    response?: string;
    alternative?: string;
  }>;
  cognitive_themes?: Array<{
    theme: string;
    description: string;
    examples?: string[];
  }>;
  protective_factors?: string[];
  mindshift_opportunities?: Array<{
    name: string;
    why_now: string;
    expected_feel: string;
    suggested_experiment: string;
    evidence?: Array<{ quote: string; entry_id?: string; date?: string }>;
  }>;
  action_suggestions?: SophiaAction[];
  journal_prompts?: string[];
  mood_insights?: {
    correlations?: string[];
    trends?: string[];
  };
  tag_analysis?: {
    most_used?: string[];
    correlations?: string[];
  };
  data_gaps?: string[];
  insufficient_data?: boolean;
  breakthroughs: string[];
  recommendations: Array<{
    action: string;
    why: string;
  }>;
  affirmation: string;
  dataAnalyzed?: {
    journalEntries: number;
    moodEntries: number;
    mindshifts: number;
    dateRange: string;
  };
  creditStatus?: any;
  requestId?: string;
}

export type SophiaCoreResponse = SophiaDeepDiveResponse;

export interface SophiaAction {
  title: string;
  description: string;
  due_date: string | null;
  duration_minutes?: number;
  recurrence?: 'daily' | 'weekly' | 'monthly';
  effort_level?: 'low' | 'medium' | 'high';
  micro_steps?: string[];
  tags: string[];
  source: 'sophia';
  dedupe_key: string;
  evidence?: Array<{
    quote: string;
    entry_id: string;
    date: string;
  }>;
}

const toText = (value: any, fallback = ''): string => {
  if (typeof value === 'string') return value.trim();
  if (value && typeof value === 'object') {
    return (
      value.text ||
      value.content ||
      value.description ||
      value.action ||
      value.title ||
      fallback
    ).toString().trim();
  }
  return fallback;
};

const normalizeAction = (value: any, index: number, prefix: string): SophiaAction => {
  const title = toText(value?.title || value?.action || value, `${prefix} ${index + 1}`);
  const description = toText(
    value?.description || value?.why || value?.content,
    'A small step to help you turn the insight into lived practice.'
  );

  return {
    title,
    description,
    due_date: value?.due_date ?? null,
    duration_minutes: typeof value?.duration_minutes === 'number' ? value.duration_minutes : 10,
    recurrence: value?.recurrence,
    effort_level: value?.effort_level || 'low',
    micro_steps: Array.isArray(value?.micro_steps)
      ? value.micro_steps.map((step: any) => toText(step, 'Take one small step')).filter(Boolean)
      : [],
    tags: Array.isArray(value?.tags) ? value.tags.filter((tag: any) => typeof tag === 'string') : ['insight'],
    source: 'sophia',
    dedupe_key: value?.dedupe_key || `${prefix.toLowerCase().replace(/\s+/g, '_')}_${index}_${Date.now()}`,
    evidence: Array.isArray(value?.evidence) ? value.evidence : undefined,
  };
};

const normalizeAskResponse = (data: any): SophiaAskResponse => {
  const answer = toText(data.answer || data.response, 'Sophia could not form a clear answer this time.');
  const actions = Array.isArray(data?.bridges?.actions_to_create)
    ? data.bridges.actions_to_create.map((action: any, index: number) => normalizeAction(action, index, 'Ask Action'))
    : Array.isArray(data?.actions)
      ? data.actions.map((action: any, index: number) => normalizeAction(action, index, 'Ask Action'))
      : [];

  return {
    ...data,
    response: data.response || answer,
    answer,
    perspective_shift: toText(data.perspective_shift || data.reframe || data.shift, ''),
    supporting_evidence: Array.isArray(data.supporting_evidence) ? data.supporting_evidence : [],
    bridges: {
      ...(data.bridges || {}),
      actions_to_create: actions,
      journal_prompt: data.bridges?.journal_prompt || data.journal_prompt,
    },
    follow_ups: Array.isArray(data.follow_ups)
      ? data.follow_ups
      : Array.isArray(data.followUp)
        ? data.followUp
        : [],
    assumptions: Array.isArray(data.assumptions) ? data.assumptions : [],
  };
};

const normalizeQuickResponse = (data: any, subtype: SophiaQuickResponse['subtype']): SophiaQuickResponse => {
  let content = toText(data.content || data.result, '');
  let items: SophiaInsightItem[] = Array.isArray(data.items) ? data.items : [];

  if (items.length === 0) {
    if (subtype === 'prompt' && data.prompt) {
      content = content || toText(data.context, 'A prompt to help you listen inward.');
      items = [{
        title: 'Journal prompt',
        content: toText(data.prompt),
        why_fits: toText(data.context, 'This helps you slow down and notice what is really here.'),
      }];
    }

    if (subtype === 'reframe' && data.reframe) {
      content = content || toText(data.reframe);
      items = [
        {
          title: 'The reframe',
          content: toText(data.reframe),
          why_fits: toText(data.technique, 'This creates a little distance between you and the thought.'),
        },
        {
          title: 'Anchor phrase',
          content: toText(data.affirmation, 'I can observe this without becoming it.'),
        },
      ];
    }

    if (subtype === 'action' && Array.isArray(data.actions)) {
      content = content || toText(data.encouragement, 'Choose one tiny action and let it change the state.');
      items = data.actions.map((action: any, index: number) => ({
        title: toText(action.action || action.title, `Action ${index + 1}`),
        content: toText(action.why || action.content, 'This gives your insight a place to land.'),
        duration_minutes: 10,
        effort_level: 'low' as const,
        micro_steps: [],
      }));
    }

    if (subtype === 'metaphor' && (data.metaphor || data.explanation || data.insight)) {
      content = content || toText(data.metaphor);
      items = [
        { title: 'Metaphor', content: toText(data.metaphor, 'A new way to see this pattern.') },
        { title: 'What it means', content: toText(data.explanation, 'This helps the pattern become visible.') },
        { title: 'Takeaway', content: toText(data.insight, 'You can meet this with more awareness than before.') },
      ].filter(item => item.content);
    }
  }

  const actionItems = subtype === 'action'
    ? items.map((item, index) => normalizeAction(item, index, 'Quick Action'))
    : [];

  return {
    ...data,
    subtype,
    content: content || items[0]?.content,
    items,
    supporting_evidence: Array.isArray(data.supporting_evidence) ? data.supporting_evidence : [],
    bridges: {
      ...(data.bridges || {}),
      actions_to_create: Array.isArray(data.bridges?.actions_to_create)
        ? data.bridges.actions_to_create.map((action: any, index: number) => normalizeAction(action, index, 'Quick Action'))
        : actionItems,
      journal_prompt: data.bridges?.journal_prompt || data.prompt,
    },
  };
};

const normalizeDeepDiveResponse = (data: any): SophiaDeepDiveResponse => {
  const recommendations = Array.isArray(data.recommendations) ? data.recommendations : [];

  return {
    ...data,
    summary: data.summary || data.headline_summary || 'Sophia found a few signals in your recent practice.',
    headline_summary: data.headline_summary || data.summary,
    patterns: Array.isArray(data.patterns)
      ? data.patterns.map((pattern: any) => ({
          ...pattern,
          name: pattern.name || pattern.pattern || 'Pattern noticed',
          description: pattern.description || pattern.significance || pattern.evidence || '',
          evidence_refs: Array.isArray(pattern.evidence_refs)
            ? pattern.evidence_refs
            : pattern.evidence
              ? [pattern.evidence]
              : [],
          strength: pattern.strength || 'emerging',
          pattern: pattern.pattern || pattern.name || 'Pattern noticed',
          evidence: pattern.evidence || '',
          significance: pattern.significance || pattern.description || '',
        }))
      : [],
    triggers: Array.isArray(data.triggers)
      ? data.triggers.map((trigger: any) => ({
          ...trigger,
          situation: trigger.situation || trigger.trigger || 'Trigger noticed',
          emotional_shift: trigger.emotional_shift || trigger.response || 'A shift in state',
          example_entry: trigger.example_entry || trigger.alternative || '',
          trigger: trigger.trigger || trigger.situation || 'Trigger noticed',
          response: trigger.response || trigger.emotional_shift || '',
          alternative: trigger.alternative || '',
        }))
      : [],
    breakthroughs: Array.isArray(data.breakthroughs) ? data.breakthroughs : [],
    recommendations,
    action_suggestions: Array.isArray(data.action_suggestions)
      ? data.action_suggestions.map((action: any, index: number) => normalizeAction(action, index, 'Deep Dive Action'))
      : recommendations.map((action: any, index: number) => normalizeAction(action, index, 'Deep Dive Action')),
    journal_prompts: Array.isArray(data.journal_prompts) ? data.journal_prompts : [],
    cognitive_themes: Array.isArray(data.cognitive_themes) ? data.cognitive_themes : [],
    protective_factors: Array.isArray(data.protective_factors) ? data.protective_factors : [],
    mindshift_opportunities: Array.isArray(data.mindshift_opportunities) ? data.mindshift_opportunities : [],
    mood_insights: data.mood_insights || { correlations: [], trends: [] },
    tag_analysis: data.tag_analysis || { most_used: [], correlations: [] },
    data_gaps: Array.isArray(data.data_gaps) ? data.data_gaps : [],
  };
};

export function normalizeSophiaResult(
  type: 'ask' | 'quick' | 'deepDive',
  result: any,
  quickType?: SophiaQuickResponse['subtype'],
): SophiaAskResponse | SophiaQuickResponse | SophiaDeepDiveResponse {
  if (type === 'ask') return normalizeAskResponse(result || {});
  if (type === 'quick') return normalizeQuickResponse(result || {}, quickType || result?.subtype || 'prompt');
  return normalizeDeepDiveResponse(result || {});
}

async function getAuthToken(): Promise<string> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.error('Error getting session:', error);
      throw new Error('Not authenticated');
    }

    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    return session.access_token;
  } catch (error) {
    console.error('Exception getting auth token:', error);
    throw new Error('Not authenticated');
  }
}

const readFunctionError = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return { error: response.statusText };
  }
};

const getFriendlyFunctionError = (error: any, fallback: string) => {
  const details = typeof error?.details === 'string' ? error.details : '';
  const message = typeof error?.message === 'string' ? error.message : '';
  const rawError = typeof error?.error === 'string' ? error.error : '';
  const combined = `${rawError} ${message} ${details}`.toLowerCase();

  if (combined.includes('missing groq_api_key')) {
    return 'Groq is not connected yet. Add GROQ_API_KEY in Supabase Edge Function secrets, then redeploy the insight functions.';
  }

  if (combined.includes('invalid_api_key') || combined.includes('api key')) {
    return 'Groq rejected the API key. Please update the GROQ_API_KEY secret in Supabase.';
  }

  if (combined.includes('content is required')) {
    return 'Write a journal entry first, then ask Sophia for a quick insight.';
  }

  return message || rawError || details || fallback;
};

export async function callAskSophia(question: string): Promise<SophiaAskResponse> {
  try {
    const token = await getAuthToken();
    const requestId = crypto.randomUUID();

    const response = await fetch(`${SUPABASE_URL}/functions/v1/insights-ask`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'apikey': SUPABASE_PUBLIC_KEY || '',
      },
      body: JSON.stringify({ question, requestId }),
    });

    if (!response.ok) {
      const error = await readFunctionError(response);
      if (response.status === 403) {
        throw new Error(error.message || 'Insufficient credits');
      }
      throw new Error(getFriendlyFunctionError(error, 'Failed to get response from Sophia'));
    }

    return normalizeAskResponse(await response.json());
  } catch (error) {
    console.error('Error calling Ask Sophia:', error);
    throw error;
  }
}

export async function callQuickInsight(
  subtype: 'prompt' | 'reframe' | 'action' | 'metaphor',
  content: string
): Promise<SophiaQuickResponse> {
  try {
    const token = await getAuthToken();
    const requestId = crypto.randomUUID();

    const response = await fetch(`${SUPABASE_URL}/functions/v1/insights-quick`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'apikey': SUPABASE_PUBLIC_KEY || '',
      },
      body: JSON.stringify({ subtype, content, requestId }),
    });

    if (!response.ok) {
      const error = await readFunctionError(response);
      if (response.status === 403) {
        throw new Error(error.message || 'Insufficient credits');
      }
      throw new Error(getFriendlyFunctionError(error, 'Failed to generate quick insight'));
    }

    return normalizeQuickResponse(await response.json(), subtype);
  } catch (error) {
    console.error('Error calling Quick Insight:', error);
    throw error;
  }
}

export async function callDeepDive(): Promise<SophiaDeepDiveResponse> {
  try {
    const token = await getAuthToken();
    const requestId = crypto.randomUUID();

    const response = await fetch(`${SUPABASE_URL}/functions/v1/insights-deep-dive`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'apikey': SUPABASE_PUBLIC_KEY || '',
      },
      body: JSON.stringify({ requestId }),
    });

    if (!response.ok) {
      const error = await readFunctionError(response);
      if (response.status === 400 && error.requirements) {
        const journalNeeded = error.requirements.journal_entries.needed;
        const moodNeeded = error.requirements.mood_check_ins.needed;
        const parts = [];
        if (journalNeeded > 0) parts.push(`${journalNeeded} more journal ${journalNeeded === 1 ? 'entry' : 'entries'}`);
        if (moodNeeded > 0) parts.push(`${moodNeeded} more mood ${moodNeeded === 1 ? 'check-in' : 'check-ins'}`);
        throw new Error(`To generate Deep Dive insights, please add ${parts.join(' and ')}.`);
      }
      if (response.status === 403) {
        throw new Error(error.message || 'Insufficient credits');
      }
      throw new Error(getFriendlyFunctionError(error, 'Failed to generate Deep Dive'));
    }

    return normalizeDeepDiveResponse(await response.json());
  } catch (error) {
    console.error('Error calling Deep Dive:', error);
    throw error;
  }
}

export function prepareDataForSophia(
  task: 'ask' | 'quick' | 'core' | 'emotions',
  timeframe: { mode: 'last_5' | 'last_7d' | 'last_30d' | 'custom'; from?: string; to?: string },
  timezone: string,
  currentDate: string,
  userProfile?: { display_name?: string; pronouns?: string },
  journalEntries?: JournalEntry[],
  mindshifts?: MindShift[],
  dailyMoods?: DailyMoodEntry[],
  actions?: Action[],
  dashboardContext?: any,
  taskSpecific?: any,
) {
  const entries = journalEntries || [];
  const shifts = mindshifts || [];
  const moods = dailyMoods || [];
  const userActions = actions || [];

  const effectiveTimeframe = task === 'core'
    ? { ...timeframe, mode: 'last_30d' as const }
    : timeframe;

  const filterByTimeframe = (items: Array<{ date: string }>) => {
    const now = new Date();
    let cutoff: Date;

    switch (effectiveTimeframe.mode) {
      case 'last_5':
        return items.slice(-5);
      case 'last_7d':
        cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'last_30d':
        if (task === 'core') {
          return items;
        }
        cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        if (effectiveTimeframe.from && effectiveTimeframe.to) {
          return items.filter(item =>
            item.date >= effectiveTimeframe.from! && item.date <= effectiveTimeframe.to!
          );
        }
        return items;
      default:
        cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    }

    return items.filter(item => new Date(item.date) >= cutoff);
  };

  const filteredEntries = filterByTimeframe(entries);
  const filteredMindshifts = filterByTimeframe(shifts);
  const filteredMoods = filterByTimeframe(moods);

  return {
    task,
    timeframe: effectiveTimeframe,
    timezone,
    current_date: currentDate,
    user_profile: userProfile,
    journal_entries: filteredEntries.map(entry => ({
      id: entry.id,
      date: entry.date,
      text: entry.content,
      tags: entry.tags || [],
      mood_label: entry.mood,
      starred: entry.isStarred,
      pinned: entry.isPinned
    })),
    mindshifts: filteredMindshifts.map(shift => ({
      id: shift.id,
      date: shift.date,
      description: shift.content,
      tags: shift.tags || [],
      category: shift.category
    })),
    mood_checkins: (task === 'core' ? moods : filteredMoods).map(mood => ({
      date: mood.date,
      energy: mood.energy,
      stress: mood.stress,
      gratitude: mood.gratitude,
      clarity: mood.clarity,
      connection: mood.connection,
      overall_mood: mood.mood
    })),
    actions: userActions.map(action => ({
      title: action.content,
      completed: action.completed,
      due_date: action.dueDate,
      overdue: action.dueDate ? new Date(action.dueDate) < new Date() && !action.completed : false,
      tags: action.tags || [],
      dedupe_key: action.content.toLowerCase().replace(/\s+/g, '_')
    })),
    dashboard_context: dashboardContext,
    ...taskSpecific,
  };
}
